import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FaCamera } from "react-icons/fa";
import defaultProfilePic from "assets/img/default-profile-pic.png";
import { useLocation, Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Collapse } from "react-bootstrap";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { openModal } from "../../../../redux/slice/managePasswordSlice";
import { fetchMyProfile } from "../../../../redux/slice/myProfileSlice";
import { fetchUserById } from "../../../../redux/slice/getUserByIdSlice";
import { getDisplayValue } from "utils/getDisplayValue";
import { updateEmployeeProfileDetails } from "../../../../redux/slice/updateEmployeeProfileSlice";
import {
  getSingleUploadedFile,
  createFilesUpload,
} from "../../../../redux/slice/filesUploadSlice";
import { toast, ToastContainer } from "react-toastify";
import EmployeeEditModal from "./EmployeeEditModal";
import { useForm } from "react-hook-form";
import EmployeeCredentialEditModal from "../../employee/EmployeeCredentialEditModal";
import { useHasPermission } from "../../../../hooks/useHasPermission";

const nomineeFields = [
  {
    label: "Nominee Name",
    name: "nomineeName",
    type: "text",
    rules: {
      required: "Nominee Name is required",
      pattern: {
        value: /^[a-zA-Z\s]*$/,
        message: "Numbers and special characters are not allowed",
      },
      minLength: { value: 2, message: "Name must be at least 2 characters" },
    },
  },
  {
    label: "Relationship",
    name: "relation",
    type: "text",
    rules: {
      required: "Relationship is required",
      pattern: {
        value: /^[a-zA-Z\s]*$/,
        message: "Numbers and special characters are not allowed",
      },
      minLength: { value: 2, message: "Name must be at least 2 characters" },
    },
  },
  {
    label: "Date of Birth",
    name: "dob",
    type: "date",
    rules: { required: "Date of Birth is required" },
  },
  {
    label: "Aadhaar No",
    name: "aadhaarNo",
    type: "text",
    rules: {
      required: "Aadhaar Number is required",
      pattern: { value: /^\d{12}$/, message: "Aadhaar must be 12 digits" },
    },
  },
  {
    label: "PAN No",
    name: "panNo",
    type: "text",
    rules: {
      required: "PAN Number is required",
      pattern: {
        value: /[A-Z]{5}[0-9]{4}[A-Z]{1}/,
        message: "Invalid PAN format",
      },
    },
  },
  {
    label: "Address",
    name: "address",
    type: "text",
    rules: { required: "Address is required" },
  },
];

const formatLabel = (value) => {
  if (!value) return "";

  return value
    // permanentEmployees -> permanent Employees
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    // INFORMATION_TECHNOLOGY -> INFORMATION TECHNOLOGY
    .replace(/_/g, " ")
    // sabko lowercase karo
    .toLowerCase()
    // har word ka first letter capital
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const bankFields = [
  {
    label: "Account Holder Name",
    name: "accountHolderName",
    type: "text",
    rules: {
      required: "Account holder name is required",
      minLength: { value: 3, message: "Name is too short" },
      pattern: {
        value: /^[a-zA-Z\s]*$/,
        message: "Only alphabets are allowed",
      },
    },
  },
  {
    label: "Account Number",
    name: "accountNumber",
    type: "text",
    maxLength: 18,
    rules: {
      required: "Account number is required",
      minLength: { value: 9, message: "Minimum 9 digits required" },
      maxLength: { value: 18, message: "Maximum 18 digits allowed" },
      pattern: { value: /^\d+$/, message: "Only digits are allowed" },
    },
  },
  {
    label: "Bank Name",
    name: "bankName",
    type: "text",
    rules: { required: "Bank name is required" },
  },
  {
    label: "IFSC Code",
    name: "ifscCode",
    type: "text",
    rules: {
      required: "IFSC code is required",
      pattern: {
        value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
        message: "Invalid IFSC (e.g., SBIN0123456)",
      },
    },
  },
  {
    label: "Branch Name",
    name: "branchName",
    type: "text",
    rules: { required: "Branch name is required" },
  },
];

const addressFields = [
  {
    label: "Address Line 1",
    name: "address1",
    type: "text",
    rules: { required: "Address Line 1 is required" },
  },
  {
    label: "Address Line 2",
    name: "address2",
    type: "text",
  },
  {
    label: "Address Line 3",
    name: "address3",
    type: "text",
  },
  {
    label: "City",
    name: "city",
    type: "text",
    rules: {
      required: "City is required",
      pattern: {
        value: /^[a-zA-Z\s]*$/,
        message: "Numbers and special characters are not allowed",
      },
    },
  },
  {
    label: "State",
    name: "state",
    type: "text",
    rules: {
      required: "State is required",
      pattern: {
        value: /^[a-zA-Z\s]*$/,
        message: "Numbers and special characters are not allowed",
      },
    },
  },
  {
    label: "Zip Code",
    name: "zip",
    type: "text",
    rules: {
      required: "Zip Code is required",
      pattern: {
        value: /^\d{6}$/,
        message: "Zip code must be exactly 6 digits",
      },
    },
  },
  {
    label: "Country",
    name: "country",
    type: "text",
    rules: {
      required: "Country is required",
      pattern: {
        value: /^[a-zA-Z\s]*$/,
        message: "Numbers and special characters are not allowed",
      },
    },
  },
];

const educationFields = [
  {
    label: "Qualification",
    name: "degreeName",
    type: "text",
    rules: {
      required: "Qualification is required",
      minLength: { value: 2, message: "Min 2 characters required" },
    },
  },
  {
    label: "Field of Study",
    name: "fieldOfStudy",
    type: "text",
    rules: {
      required: "Field of study is required",
      pattern: {
        value: /^[a-zA-Z\s\.]*$/,
        message: "Numbers and special characters not allowed",
      },
    },
  },
  {
    label: "Institution Name",
    name: "institutionName",
    type: "text",
    rules: { required: "Institution name is required" },
  },
  {
    label: "Location",
    name: "location",
    type: "text",
    rules: {
      pattern: {
        value: /^[a-zA-Z\s,]*$/,
        message: "Invalid format for location",
      },
    },
  },
  {
    label: "Grade / CGPA",
    name: "gradeOrcgpa",
    type: "text",
    rules: { required: "Grade/CGPA is required" },
  },
  {
    label: "From Date",
    name: "fromDate",
    type: "date",
    rules: { required: "Start date is required" },
  },
  {
    label: "To Date",
    name: "toDate",
    type: "date",
    rules: {
      required: "End date is required",
      validate: (value, formValues) => {
        const start = formValues.fromDate;
        if (!start) return true;
        return (
          new Date(value) >= new Date(start) ||
          "End date cannot be earlier than start date"
        );
      },
    },
  },
  {
    label: "Roll Number",
    name: "rollNumber",
    type: "text",
  },
];

function InfoRow({ label, value }) {
  return (
    <div className="d-flex align-items-center justify-content-between mb-2">
      <span className="profile-label text-nowrap profile-label mr-2">
        {label}:{" "}
      </span>
      <p
        className="profile-label-value text-theme m-0 text-end text-break text-right"
        style={{ maxWidth: "70%" }}
        title={getDisplayValue(value)}
      >
        {getDisplayValue(value) ?? "N/A"}
      </p>
    </div>
  );
}

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
};

function InfoRowPersonal({ label, value, className = "" }) {
  return (
    <div
      className={`col-md-6 d-flex align-items-center justify-content-between mb-2 unique-personal-row-item ${className}`}
    >
      <span className="d-inline-flex align-items-center unique-personal-label">
        {label}:
      </span>
      <p
        className="text-theme m-0 unique-personal-value text-end text-align-left"
        title={getDisplayValue(value)}
      >
        {getDisplayValue(value)}
      </p>
    </div>
  );
}

InfoRowPersonal.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  className: PropTypes.string,
};

export default function Profile() {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const canEditEmpCredential =
    useHasPermission("Employee Details-manage") ||
    useHasPermission("Existing Employee-manage");
  const canViewRole = useHasPermission("Role-view");
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      familyDetails: [],
    },
  });

  const idFromLocation = location?.state?.id;
  const idFromParams = params?.id; // EmpID
  const idToUse = idFromParams ?? idFromLocation; // user ID
  const { Myprofile } = useSelector((state) => state.Myprofile ?? {});
  const { User } = useSelector((state) => state.User ?? {});
  const userId = useSelector(
    (state) => state?.persistedReducer?.forms?.savedPermissions?.userId ?? "",
  );
  useSelector((state) =>
    console.log(
      "state?.persistedReducer?.forms?.savedPermissions",
      state?.persistedReducer?.forms?.savedPermissions,
    ),
  );
  const displayData = idToUse ? User : Myprofile;
  const [open, setOpen] = useState({
    about: true,
    personal: false,
    bank: false,
    family: false,
    education: false,
    experience: false,
    address: false,
    additional: false,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [credentialModal, setCredentialModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentFields, setCurrentFields] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [modalSize, setModalSize] = useState("md");
  const [modalType, setModalType] = useState("");
  const [showEditIcon, setShowEditIcon] = useState(false);
  const [selectedProfileImage, setSelectedProfileImage] = useState(null);
  useEffect(() => {
    setShowEditIcon(displayData?.documentUploadStatus === "EXISTING_EMP");
  }, [displayData]);
  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        const response = await dispatch(
          getSingleUploadedFile({
            type: "passportphoto",
            id: idToUse ?? userId,
          }),
        ).unwrap();
        if (response && response.fileUrl) {
          setSelectedProfileImage(response.fileUrl);
        } else {
          setSelectedProfileImage(null);
        }
      } catch (err) {
        console.error("Image fetch failed", err);
        setSelectedProfileImage(null);
      }
    };

    if (idToUse) {
      dispatch(fetchUserById(idToUse));
    } else {
      dispatch(fetchMyProfile());
    }
    fetchProfileImage();
    return () => {
      if (selectedProfileImage) {
        URL.revokeObjectURL(selectedProfileImage);
      }
    };
  }, [idToUse, dispatch]);

  const formatDate = (iso) => {
    if (!iso) return "N/A";

    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "N/A";

    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-IN", { month: "short" });
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const extractYear = (str) => {
    if (!str) return "N/A";
    return str.split("-")[0];
  };

  const getFormattedDuration = (fromDate, toDate) => {
    if (!fromDate) return "N/A";
    const start = new Date(fromDate);
    const end = toDate ? new Date(toDate) : new Date();
    if (isNaN(start) || isNaN(end)) return "N/A";
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    if (months < 0) {
      years--;
      months += 12;
    }
    let duration = "";
    if (years > 0) duration += `${years}-yr`;
    if (months > 0) duration += `${years > 0 ? " - " : ""}${months}-month`;
    if (!duration) duration = "0-month";

    const format = (date) =>
      `${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
    return `${duration} (${format(start)}-${format(end)})`;
  };

  const toggle = (key) =>
    setOpen((p) => ({
      ...p,
      [key]: !p[key],
    }));

  const handleProfileImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedProfileImage(URL.createObjectURL(file));
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "passportphoto");
      formData.append("id", String(idToUse ?? userId));

      await dispatch(createFilesUpload(formData)).unwrap();
      toast.success(`Passport photo uploaded successfully!`, {
        className: "toast-success-custom",
      });
    } catch (err) {
      toast.error(err?.message || `Failed in uploading Passport photo!`, {
        className: "toast-error-custom",
        autoClose: 5000,
      });
    }
  };

  const joinAddress = (addr) => {
    if (!addr || Object.keys(addr).length === 0) return "N/A";
    const parts = [
      addr.address1,
      addr.address2,
      addr.address3,
      addr.city,
      addr.state,
      addr.country,
      addr.zip,
    ].filter(
      (part) =>
        part !== null && part !== undefined && String(part).trim() !== "",
    );
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  const designation = displayData?.employeeDetails?.designation;
  const role = displayData?.roleName;

  // const designationText = designation
  //   ? `${getDisplayValue(designation)} / ${getDisplayValue(role)}`
  //   : getDisplayValue(role);

  const designationText = designation
    ? `${getDisplayValue(designation)}`
    : '-NA-';

  const handleImageError = (e) => {
    e.target.src = defaultProfilePic;
  };

  const emergency1 = displayData?.addresses?.[0];
  const emergency2 = displayData?.addresses?.[1];

  const isSameEmergency =
    emergency1?.emergencyContactNumber &&
    emergency2?.emergencyContactNumber &&
    emergency1.emergencyContactNumber === emergency2.emergencyContactNumber;

  const onUpdate = async (data) => {
    const employeeId = idToUse ?? userId;
    let payload = {};
    const existingExperience = displayData?.employmentHistory || [];
    if (modalType === "bank") {
      payload = {
        bankDetails: {
          ...data,
        },
      };
    } else if (modalType === "nominee") {
      payload = {
        nominees: Array.isArray(data.nomineeDetails)
          ? data.nomineeDetails
          : [data],
      };
    } else if (modalType === "family") {
      payload = {
        insuranceDependentDetails: data.familyDetails,
      };
    } else if (modalType === "address") {
      payload = {
        addresses: [
          {
            ...displayData?.addresses?.[0],
          },
          {
            ...data,
          },
        ],
      };
    } else if (modalType === "experience") {
      const newExperiences = data.employmentHistory || [];

      payload = {
        employmentHistory: [...existingExperience, ...newExperiences],
      };
    } else if (modalType === "education") {
      const existingEducation = displayData?.educationDetails || [];
      const newEducation = data.educationDetails || [];

      payload = {
        educationDetails: [...existingEducation, ...newEducation],
      };
    } else {
      payload = data;
    }

    await dispatch(updateEmployeeProfileDetails({ employeeId, payload }))
      .unwrap()
      .then(() => {
        setShowEditModal(false);
      })
      .catch((error) => {
        console.error("Update failed:", error);
      });

    if (idToUse) {
      dispatch(fetchUserById(idToUse));
    } else {
      dispatch(fetchMyProfile());
    }
  };

  const handleEditClick = (e, type, data) => {
    e.stopPropagation();
    setModalType(type);
    const cleanNumbers = (val) => (val ? String(val).replace(/\D/g, "") : "");
    if (type === "family") {
      setModalTitle("Edit Family Information");
      setModalSize("lg");
      reset({
        familyDetails: data.map((member) => ({
          ...member,
          dob: member.dob
            ? new Date(member.dob).toISOString().split("T")[0]
            : "",
        })),
      });
    } else if (type === "nominee") {
      setModalTitle("Edit Nominee Information");
      setCurrentFields(nomineeFields);
      setModalSize("md");
      const formatDate = (dateStr) =>
        dateStr ? new Date(dateStr).toISOString().split("T")[0] : "";
      reset({
        ...data,
        dob: formatDate(data?.dob),
        aadhaarNo: cleanNumbers(data?.aadhaarNo),
      });
    } else if (type === "bank") {
      setModalTitle("Edit Bank Information");
      setCurrentFields(bankFields);
      setModalSize("md");
      reset(data);
    } else if (type === "address") {
      setModalTitle("Edit Current Address");
      setModalType("address");
      setModalSize("md");
      setCurrentFields(addressFields);
      reset(data);
    } else if (type === "experience") {
      setModalTitle("Add New Experience");
      setModalType("experience");
      setModalSize("lg");
      setCurrentFields(experienceFields);
      reset({
        employmentHistory: [
          {
            previousEmployerName: "",
            department: "",
            designation: "",
            employeeCode: "",
            employmentPeriodFrom: "",
            employmentPeriodTo: "",
            managerName: "",
            mobileNumber: "",
            managerEmail: "",
          },
        ],
      });
    } else if (type === "education") {
      setModalTitle("Add Education Details");
      setModalType("education");
      setModalSize("lg");
      setCurrentFields(educationFields);
      reset({
        educationDetails: [
          {
            degreeName: "",
            fieldOfStudy: "",
            institutionName: "",
            gradeOrcgpa: "",
            fromDate: "",
            toDate: "",
            rollNumber: "",
            location: "",
          },
        ],
      });
    }

    setShowEditModal(true);
  };
  const formatManagerName = (value) => {
    if (!value) return "N/A";

    if (value.includes("-")) {
      const [name, role] = value.split("-");

      return `${name.trim()} (${role.trim()})`;
    }

    return value;
  };
  console.log("currentFields", currentFields);
  const experienceFields = [
    {
      label: "Employer Name",
      name: "previousEmployerName",
      type: "text",
      rules: {
        required: "Employer name is required",
        minLength: { value: 2, message: "Name is too short" },
      },
    },
    {
      label: "Designation",
      name: "designation",
      type: "text",
      rules: {
        required: "Designation is required",
        pattern: {
          value: /^[a-zA-Z\s]*$/,
          message: "Numbers and special characters not allowed",
        },
      },
    },
    {
      label: "Department",
      name: "department",
      type: "text",
      rules: {
        pattern: {
          value: /^[a-zA-Z\s]*$/,
          message: "Numbers and special characters not allowed",
        },
      },
    },
    {
      label: "Employee Code",
      name: "employeeCode",
      type: "text",
    },
    {
      label: "From Date",
      name: "employmentPeriodFrom",
      type: "date",
      rules: { required: "Start date is required" },
    },
    {
      label: "To Date",
      name: "employmentPeriodTo",
      type: "date",
      rules: {
        required: "End date is required",
        validate: (value) => {
          const fromDate = watch("employmentPeriodFrom");
          if (!fromDate) return true;
          return (
            new Date(value) >= new Date(fromDate) ||
            "End date cannot be earlier than start date"
          );
        },
      },
    },
    {
      label: "Manager Name",
      name: "managerName",
      type: "text",
      rules: {
        pattern: {
          value: /^[a-zA-Z\s]*$/,
          message: "Numbers and special characters not allowed",
        },
      },
    },
    {
      label: "Manager Email",
      name: "managerEmail",
      type: "text",
      rules: {
        pattern: { value: /^\S+@\S+\.\S+$/, message: "Invalid email format" },
      },
    },
    {
      label: "Manager Contact",
      name: "mobileNumber",
      type: "text",
      rules: {
        pattern: { value: /^[0-9]{10}$/, message: "Must be 10 digits" },
      },
    },
  ];
  return (
    <div className="container-fluid">
      <div className="row clearfix">
        <div className="col-md-4 mt-2">
          <div className="profile-card border-bottom">
            <div className="profile-header">
              <div className="profile-pic-section">
                <img
                  src={selectedProfileImage ?? defaultProfilePic}
                  alt="profile"
                  className={`${selectedProfileImage === defaultProfilePic ? "default-profile-image" : "profile-image"} unique-profile-image`}
                  width={140}
                  height={140}
                  style={{ objectFit: "cover" }}
                  onError={handleImageError}
                />
                <input
                  type="file"
                  accept="image/*"
                  id="fileInputUnique"
                  className="file-input"
                  onChange={handleProfileImageChange}
                  aria-label="Change profile image"
                />
                <label
                  htmlFor="fileInputUnique"
                  className="camera-icon"
                  title="Upload"
                >
                  <FaCamera size={20} className="cursor-pointer" />
                </label>
              </div>
            </div>

            <div className="p-2 d-flex flex-column align-items-center justify-content-center w-100">
              <h5 className="mb-2 fw-bold text-dark">
                {getDisplayValue(displayData?.fullName)}
              </h5>
              <div className="d-flex justify-content-center gap-5">
                <span className="desg-name badge me-2">{formatLabel(designationText)}</span>
              </div>
            </div>

            <div className="w-100 p-2">
              <InfoRow
                label="Employee ID"
                value={displayData?.employeeDetails?.employeeId}
              />
              <InfoRow label="Official Email" value={displayData?.email} />
              <InfoRow
                label="Role"
                value={formatLabel(displayData?.roleName)}
              />
              <InfoRow
                label="Department"
                value={formatLabel(displayData?.employeeDetails?.department)}
              />
              <InfoRow
                label="Joining Date"
                value={formatDate(displayData?.joinedDate)}
              />
              <InfoRow
                label="Manager"
                value={formatManagerName(
                  displayData?.employeeDetails?.reportManager,
                )}
              />
              <InfoRow
                label="Team Lead"
                value={formatManagerName(
                  displayData?.employeeDetails?.teamLead,
                )}
              />
              <InfoRow
                label="Location"
                value={formatLabel(displayData?.employeeDetails?.officeLocation?.charAt(0).toUpperCase() + displayData?.employeeDetails?.officeLocation?.slice(1))}
              />
              <InfoRow
                label="Working Mode"
                value={formatLabel(displayData?.employeeDetails?.workingMode)}
              />
              <InfoRow
                label="Shift Time"
                value={formatLabel(displayData?.employeeDetails?.shiftTime)}
              />
              <InfoRow
                label="Source"
                value={formatLabel(displayData?.source)}
              />
              <InfoRow
                label="Employee Type"
                value={formatLabel(displayData?.employeeDetails?.empType)}
              />

              <div className="d-flex align-items-center justify-content-between mb-2">
                {!idToUse && (
                  <span className="d-inline-flex align-items-end">
                    <i className="bi bi-check2-circle"
                      style={{ marginRight: "8px" }}
                    />
                    <Link
                      className="password-link"
                      onClick={() => dispatch(openModal({ isForNewUser: false }))}
                    >
                      Manage Password
                    </Link>
                  </span>
                )}
                {canEditEmpCredential && idToUse && (
                  <i
                    className="bi bi-pencil fs-6 mr-4 edit-icon"
                    style={{ cursor: "pointer", fontSize: "14px", marginTop: "15px", color: "#512F9F" }}
                    onClick={() => {
                      setSelectedEmployee(displayData?.employeeDetails);
                      setCredentialModal(true);
                    }}
                  >Edit</i>
                )}
              </div>
            </div>
          </div>

          <div className="profile-card border-bottom mt-2">
            <div className="w-100 p-2">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="m-0">Basic Information</h6>
              </div>

              <InfoRow label="Mobile" value={displayData?.mobile} />
              <InfoRow
                label="Personal Email"
                value={displayData?.personalEmail}
              />
              <InfoRow
                label="Gender"
                value={displayData?.personalDetail?.gender}
              />
              <InfoRow
                label="Birthday"
                value={formatDate(displayData?.dateOfBirth)}
              />
            </div>
          </div>

          <div className="profile-card border-bottom mt-2">
            <div className="w-100 p-2">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="m-0">Emergency Contact Number</h6>
              </div>

              {emergency1 || emergency2 ? (
                <>
                  {[emergency1, emergency2]
                    .filter(
                      (contact, index, self) =>
                        contact &&
                        index ===
                        self.findIndex(
                          (c) =>
                            c?.emergencyContactNumber ===
                            contact?.emergencyContactNumber,
                        ),
                    )
                    .map((contact, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between mb-2"
                      >
                        <span className="profile-label">
                          {getDisplayValue(contact?.emergencyContactName) ||
                            "N/A"}
                        </span>

                        <p className="text-dark m-0 profile-label-value">
                          {getDisplayValue(contact?.emergencyContactNumber) ||
                            "N/A"}
                        </p>
                      </div>
                    ))}
                </>
              ) : (
                <div className="text-muted">
                  Emergency Contact not available
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-8 mt-2">
          <Card className="shadow-sm rounded-3 w-full">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("about")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">About Employee</h5>
                </Col>
                <Col
                  xs="auto"
                  className="profile-label-value d-flex align-items-center gap-3"
                >
                  {open.about ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>
              <Collapse in={open.about}>
                <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-2">
                  {displayData?.employeeDetails?.aboutUs ?? " "}
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("personal")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">Personal Information</h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {open.personal ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>
              <Collapse in={open.personal}>
                <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-2">
                  <div className="row">
                    <InfoRowPersonal
                      label="Father"
                      value={displayData?.personalDetail?.fatherName}
                    />
                    <InfoRowPersonal
                      label="Mother"
                      value={displayData?.personalDetail?.motherName}
                    />
                    <InfoRowPersonal
                      label="Aadhaar"
                      value={displayData?.personalDetail?.adhaarNo}
                    />
                    <InfoRowPersonal
                      label="PAN"
                      value={displayData?.personalDetail?.panNo}
                    />
                    <InfoRowPersonal
                      label="UAN"
                      value={displayData?.personalDetail?.uanNo}
                    />
                    <InfoRowPersonal
                      label="Religion"
                      value={displayData?.personalDetail?.religion}
                    />
                    <InfoRowPersonal
                      label="Blood Group"
                      value={displayData?.personalDetail?.bloodGroup}
                    />
                    <InfoRowPersonal
                      label="Gender"
                      value={displayData?.personalDetail?.gender}
                    />
                    <InfoRowPersonal
                      label="Marital Status"
                      value={displayData?.personalDetail?.maritalStatus}
                    />
                    <InfoRowPersonal
                      label="Employment of Spouse"
                      value={displayData?.personalDetail?.employmentOfSpouse}
                    />
                    <InfoRowPersonal
                      label="No. of Children"
                      value={displayData?.personalDetail?.noOfChildren}
                    />
                    <InfoRowPersonal
                      label="Nationality"
                      value={displayData?.addresses?.[0]?.nationality}
                    />
                    <InfoRowPersonal
                      label="Alternative Mob. No."
                      value={displayData?.personalDetail?.secondaryMobile}
                    />
                  </div>
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("bank")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">Bank Information</h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {showEditIcon && (
                    <i
                      className="bi bi-pencil fs-6 mr-4"
                      onClick={(e) =>
                        handleEditClick(e, "bank", displayData?.bankDetails)
                      }
                    ></i>
                  )}
                  {open.bank ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>

              <Collapse in={open.bank}>
                <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-2">
                  <InfoRow
                    label="Account Holder Name"
                    value={displayData?.bankDetails?.accountHolderName}
                  />
                  <InfoRow
                    label="Account Number"
                    value={displayData?.bankDetails?.accountNumber}
                  />
                  <InfoRow
                    label="Bank Name"
                    value={displayData?.bankDetails?.bankName}
                  />
                  <InfoRow
                    label="IFSC Code"
                    value={displayData?.bankDetails?.ifscCode}
                  />
                  <InfoRow
                    label="Branch Name"
                    value={displayData?.bankDetails?.branchName}
                  />
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          {/* <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("familyDetails")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">
                    Family Details
                  </h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {showEditIcon && (<i
                    className="bi bi-pencil fs-6 mr-4"
                    onClick={(e) =>
                      handleEditClick(e, "familyDetails", displayData?.familyDetails)
                    }
                  ></i>)}
                  {open.familyDetails ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>
              <Collapse in={open.familyDetails}>
                <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-2">
                  <hr className="w-100" />
                  {Array.isArray(displayData?.familyDetails) &&
                    displayData.familyDetails.length > 0 ? (
                    displayData.familyDetails.map((member, index) => (
                      <div key={member?.id ?? index}>
                        <div className="row">
                          <div className="col-md-3 col-6 text-muted profile-label">Name:</div>
                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.name)}
                          </div>
                          <div className="col-md-3 col-6 text-muted profile-label">Relationship:</div>
                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.relationship)}
                          </div>


                        </div>
                        <div className="row">
                          <div className="col-md-3 col-6 text-muted profile-label">Date of Birth:</div>

                          <div className="col-md-3 col-6 text-theme">
                            {formatDate(member?.dob)}
                          </div>
                          <div className="col-md-3 col-6 text-muted profile-label">Gender:</div>
                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.gender)}
                          </div>
                        </div> <div className="row">
                          <div className="col-md-3 col-6 text-muted profile-label">Age:</div>
                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.age)}
                          </div>
                          <div className="col-md-3 col-6 text-muted profile-label">Phone:</div>
                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.phone)}
                          </div>

                        </div>
                        <div className="row">
                          <div className="col-md-3 col-6 text-muted profile-label">SI Amount:</div>
                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.amount)}
                          </div>
                        </div>
                        {index !== displayData.insuranceDependentDetails.length - 1 && (
                          <hr className="w-100" />
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-muted text-center py-2">
                      No family details available
                    </div>
                  )}
                </div>
              </Collapse>
            </Card.Body>
          </Card> */}

          <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("insuranceDependentDetails")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">
                    Insurance Dependent Details (
                    <small>Used for Medical Insurance</small>)
                  </h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {showEditIcon && (
                    <i
                      className="bi bi-pencil fs-6 mr-4"
                      onClick={(e) =>
                        handleEditClick(
                          e,
                          "insuranceDependentDetails",
                          displayData?.insuranceDependentDetails,
                        )
                      }
                    ></i>
                  )}
                  {open.insuranceDependentDetails ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>
              <Collapse in={open.insuranceDependentDetails}>
                <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-2">
                  <hr className="w-100" />
                  {Array.isArray(displayData?.insuranceDependentDetails) &&
                    displayData.insuranceDependentDetails.length > 0 ? (
                    displayData.insuranceDependentDetails.map(
                      (member, index) => (
                        <div key={member?.id ?? index}>
                          <div className="row">
                            <div className="col-md-3 col-6 text-muted profile-label">
                              Name:
                            </div>
                            <div className="col-md-3 col-6 text-theme">
                              {getDisplayValue(member?.name)}
                            </div>
                            <div className="col-md-3 col-6 text-muted profile-label">
                              Relationship:
                            </div>
                            <div className="col-md-3 col-6 text-theme">
                              {getDisplayValue(member?.relationship)}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-3 col-6 text-muted profile-label">
                              Date of Birth:
                            </div>
                            <div className="col-md-3 col-6 text-theme">
                              {formatDate(member?.dob)}
                            </div>
                            <div className="col-md-3 col-6 text-muted profile-label">
                              Gender:
                            </div>
                            <div className="col-md-3 col-6 text-theme">
                              {getDisplayValue(member?.gender)}
                            </div>
                          </div>{" "}
                          <div className="row">
                            <div className="col-md-3 col-6 text-muted profile-label">
                              Age:
                            </div>
                            <div className="col-md-3 col-6 text-theme">
                              {getDisplayValue(member?.age)}
                            </div>
                            <div className="col-md-3 col-6 text-muted profile-label">
                              Phone:
                            </div>
                            <div className="col-md-3 col-6 text-theme">
                              {getDisplayValue(member?.phone)}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-3 col-6 text-muted profile-label">
                              SI Amount:
                            </div>
                            <div className="col-md-3 col-6 text-theme">
                              {getDisplayValue(member?.amount)}
                            </div>
                          </div>
                          {index !==
                            displayData.insuranceDependentDetails.length -
                            1 && <hr className="w-100" />}
                        </div>
                      ),
                    )
                  ) : (
                    <div className="text-muted text-center py-2">
                      No insurance dependent details available
                    </div>
                  )}
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("nominee")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">Nominee Information</h5>
                </Col>

                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {showEditIcon && (
                    <i
                      className="bi bi-pencil fs-6 mr-4 cursor-pointer"
                      onClick={(e) =>
                        handleEditClick(
                          e,
                          "nominee",
                          displayData?.nomineeDetails?.[0],
                        )
                      }
                    ></i>
                  )}

                  {open.nominee ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>

              <Collapse in={open.nominee}>
                <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-2">
                  <hr className="w-100" />

                  {Array.isArray(displayData?.nomineeDetails) &&
                    displayData.nomineeDetails.length > 0 ? (
                    displayData.nomineeDetails.map((member, index) => (
                      <div key={member?.id ?? index}>
                        <div className="row">
                          <div className="col-md-3 col-6 text-muted profile-label">
                            Name:
                          </div>

                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.nomineeName)}
                          </div>

                          <div className="col-md-3 col-6 text-muted profile-label">
                            Relationship:
                          </div>

                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.relation)}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-3 col-6 text-muted profile-label">
                            Date of Birth:
                          </div>

                          <div className="col-md-3 col-6 text-theme">
                            {formatDate(member?.dob)}
                          </div>

                          <div className="col-md-3 col-6 text-muted profile-label">
                            Aadhaar No:
                          </div>

                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.aadhaarNo)}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-3 col-6 text-muted profile-label">
                            PAN No:
                          </div>
                          <div className="col-md-3 col-6 text-theme">
                            {getDisplayValue(member?.panNo)}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-3 col-4 text-muted profile-label">
                            Address:
                          </div>
                          <div className="col-md-9 col-12 text-theme">
                            {getDisplayValue(member?.address)}
                          </div>
                        </div>
                        {index !== displayData.nomineeDetails.length - 1 && (
                          <hr className="w-100" />
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-muted text-center py-2">
                      No nominee details available
                    </div>
                  )}
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("education")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">Education Details</h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {showEditIcon && (
                    <i
                      className="bi bi-pencil fs-3 mr-4 cursor-pointer"
                      onClick={(e) => handleEditClick(e, "education", {})}
                    ></i>
                  )}
                  {open.education ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>
              <Collapse in={open.education}>
                <div className="mt-3 my-2 px-3 text-secondary ps-4 pe-3 pb-2">
                  {(displayData?.educationDetails ?? []).length > 0 ? (
                    displayData.educationDetails.map((education) => {
                      const startYear = extractYear(education?.fromDate);
                      const endYear = extractYear(education?.toDate);
                      return (
                        <div
                          className="border-bottom pb-3 mb-3"
                          key={education.id}
                        >
                          <div className="row">
                            <InfoRowPersonal
                              label="Qualification"
                              value={
                                <>
                                  {education?.degreeName}
                                  <span className="badge text-dark ms-2 pr-0">
                                    Grade/Cgpa: {education?.gradeOrcgpa}
                                  </span>
                                </>
                              }
                            />
                            <InfoRowPersonal
                              label="Institute"
                              value={
                                <>
                                  {education?.institutionName}
                                  <span className="badge text-dark ms-2">
                                    {education?.location}
                                  </span>
                                </>
                              }
                            />
                            <InfoRowPersonal
                              label="Field Of Study"
                              value={education?.fieldOfStudy}
                            />
                            <InfoRowPersonal
                              label="Duration"
                              value={`${startYear} - ${endYear}`}
                            />
                            <InfoRowPersonal
                              label="Roll Number"
                              value={education?.rollNumber}
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-muted text-center py-2">
                      No education records found
                    </div>
                  )}
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("experience")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">Experience</h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {showEditIcon && (
                    <i
                      className="bi bi-pencil fs-3 mr-4 cursor-pointer"
                      onClick={(e) => handleEditClick(e, "experience", {})}
                    ></i>
                  )}
                  {open.experience ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>
              <Collapse in={open.experience}>
                <div className="mt-3 my-2 px-3 text-secondary ps-4 pe-3 pb-2">
                  {(displayData?.employmentHistory ?? []).length > 0 ? (
                    displayData.employmentHistory.map((exp) => {
                      const startYear = extractYear(exp?.employmentPeriodFrom);
                      const endYear = extractYear(exp?.employmentPeriodTo);
                      return (
                        <div className="border-bottom pb-3 mb-3" key={exp.id}>
                          <div className="row">
                            <InfoRowPersonal
                              label="Employer"
                              value={exp?.previousEmployerName}
                              className="exp-employer"
                            />
                            <InfoRowPersonal
                              label="Employer Phone No."
                              value={exp?.mobileNumber}
                              className="exp-employer"
                            />
                            <InfoRowPersonal
                              label="Designation"
                              value={exp?.designation}
                              className="exp-designation"
                            />
                            <InfoRowPersonal
                              label="Emp Code"
                              value={exp?.employeeCode}
                              className="exp-empcode"
                            />
                            <InfoRowPersonal
                              label="Department"
                              value={exp?.department}
                              className="exp-department"
                            />
                            <InfoRowPersonal
                              label="Manager"
                              value={
                                <>
                                  {exp?.managerName}
                                  <span className="badge text-dark ms-2 exp-manager-contact text-break">
                                    {exp?.managerContactNo} |{" "}
                                    {exp?.managerEmail}
                                  </span>
                                </>
                              }
                              className="exp-manager"
                            />
                            <InfoRowPersonal
                              label="Duration"
                              value={getFormattedDuration(
                                exp?.employmentPeriodFrom,
                                exp?.employmentPeriodTo,
                              )}
                              className="exp-duration"
                            />
                            <InfoRowPersonal
                              label="Reason for leaving"
                              value={exp?.reasonForLeaving}
                              className="exp-department"
                            />
                            <InfoRowPersonal
                              label="Position"
                              value={exp?.positionType}
                              className="exp-department"
                            />
                            <InfoRowPersonal
                              label="Office Address"
                              value={exp?.address}
                              className="exp-department"
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-muted text-center py-2">
                      No experience records found
                    </div>
                  )}
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("address")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">Address</h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {showEditIcon && (
                    <i
                      className="bi bi-pencil fs-3 mr-4 cursor-pointer"
                      onClick={(e) =>
                        handleEditClick(
                          e,
                          "address",
                          displayData?.addresses?.[1],
                        )
                      }
                    ></i>
                  )}
                  {open.address ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>
              <Collapse in={open.address}>
                <div className="p-3">
                  <strong className="profile-label">
                    <i className="bi bi-geo-alt me-1" /> Permanent Address:
                  </strong>
                  <p>
                    {joinAddress(
                      displayData?.addresses?.find(
                        (addr) => addr.addressType === "PERMANENT",
                      ) ||
                      displayData?.addresses?.[0] ||
                      {},
                    )}
                  </p>

                  <strong className="profile-label">
                    <i className="bi bi-geo-alt me-1" /> Current Address:
                  </strong>
                  <p>
                    {joinAddress(
                      displayData?.addresses?.find(
                        (addr) => addr.addressType === "CURRENT",
                      ) ||
                      displayData?.addresses?.[1] ||
                      {},
                    )}
                  </p>
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-1 rounded-3 mb-3">
            <Card.Body className="p-0">
              <Row
                className="align-items-center m-0 cursor-pointer"
                onClick={() => toggle("additional")}
              >
                <Col className="col-heading p-3">
                  <h5 className="mb-0">Additional Information</h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-3">
                  {open.additional ? (
                    <BsChevronUp size={16} />
                  ) : (
                    <BsChevronDown size={16} />
                  )}
                </Col>
              </Row>

              <Collapse in={open.additional}>
                <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-2">
                  <div className="row">
                    <div className="col-md-12 d-flex align-items-center justify-content-between mb-2">
                      <span className="profile-label d-lex align-items-center profile-label">
                        <i className="bi bi-passport me-3" /> Passport Number
                      </span>
                      <p className="profile-label-value text-dark m-0 text-end">
                        {getDisplayValue(
                          displayData?.additionalDetails?.passportNumber,
                        )}
                      </p>
                    </div>
                    <div className="col-md-12 d-flex align-items-center justify-content-between mb-2">
                      <span className="profile-label d-lex align-items-center profile-label">
                        <i className="bi bi-passport me-3" /> Illness
                      </span>
                      <p className="profile-label-value text-dark m-0">
                        {getDisplayValue(
                          displayData?.additionalDetails?.illnessDeclaration,
                        )}
                      </p>
                    </div>

                    <div className="col-md-12 d-flex flex-column mb-2">
                      <span className="profile-label d-lex align-items-center mb-2 text-dark">
                        <i className="bi bi-passport me-3" /> Hobbies:
                      </span>
                      <p className="profile-label-value text-dark m-0 pl-2">
                        {getDisplayValue(
                          displayData?.additionalDetails?.hobbiesDeclaration,
                        )}
                      </p>
                    </div>

                    {/* <hr className="w-100" />
                    <div className="col-md-12 mt-3">
                      <div className="profile-label d-lex align-items-center profile-label">
                        <i className="bi bi-people me-3" /> Professional
                        References
                      </div>

                      {(displayData?.professionalReferences ?? []).length >
                        0 ? (
                        <div className="row">
                          <InfoRowPersonal
                            label="Name"
                            value={
                              displayData?.professionalReferences?.[0]?.name
                            }
                          />
                          <InfoRowPersonal
                            label="Email"
                            value={
                              displayData?.professionalReferences?.[0]?.email
                            }
                          />
                          <InfoRowPersonal
                            label="Contact"
                            value={
                              displayData?.professionalReferences?.[0]
                                ?.contactNumber
                            }
                          />
                          <InfoRowPersonal
                            label="Designation"
                            value={
                              displayData?.professionalReferences?.[0]
                                ?.designation
                            }
                          />
                          <InfoRowPersonal
                            label="Relationship"
                            value={
                              displayData?.professionalReferences?.[0]
                                ?.relationship
                            }
                          />
                          <InfoRowPersonal
                            label="Employee ID"
                            value={
                              displayData?.professionalReferences?.[0]
                                ?.employeeId
                            }
                          />
                          <InfoRowPersonal
                            label="Department"
                            value={
                              displayData?.professionalReferences?.[0]
                                ?.department
                            }
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center py-2">
                          No professional references found
                        </p>
                      )}
                    </div> */}
                  </div>
                </div>
              </Collapse>
            </Card.Body>
          </Card>

          {/* <div className="container p-0 unique-assets-container">
            <Row>
              <Col lg={12}>
                <Card className="shadow-sm border-5 rounded-3 unique-assets-card">
                  <Card.Body className="p-0">
                    <Row className="align-items-center m-0 unique-assets-header">
                      <Col className="col-heading p-3 unique-assets-heading">
                        <h5 className="mb-0 fw-bold text-dark">Assets</h5>
                      </Col>
                    </Row>
                    <div className="mt-3 text-secondary unique-assets-list">
                      <div className="border border-gray-900 unique-asset-item">
                        <div className="card-body unique-asset-body">
                          <h5 className="itemBold card-drop-title text-dark unique-asset-title">Dell Laptop - #343556656</h5>
                          <p className="card-text mb-0 justify-content-between d-flex unique-asset-meta">
                            <div>
                              <strong>Assigned on: </strong>
                              <span>22 Nov, 2022 10:32 AM</span>
                            </div>
                            <div>
                              <strong>Assigned by:</strong>
                              <span> Andrew Symon</span>
                            </div>
                          </p>
                        </div>
                      </div>
                      <br />
                      <div className="border border-gray-500 unique-asset-item">
                        <div className="card-body unique-asset-body">
                          <h5 className="itemBold card-drop-title text-dark unique-asset-title">
                            HP Bluetooth Mouse - #478878
                          </h5>
                          <p className="card-text mb-0 justify-content-between d-flex unique-asset-meta">
                            <div>
                              <strong>Assigned on: </strong>
                              <span>22 Nov, 2022 10:32 AM</span>
                            </div>
                            <div>
                              <strong>Assigned by:</strong>
                              <span> Andrew Symon</span>
                            </div>
                          </p>
                        </div>
                      </div>
                      <br />
                      <div className="border border-gray-500 unique-asset-item">
                        <div className="card-body unique-asset-body">
                          <h5 className="itemBold card-drop-title text-dark unique-asset-title">
                            HP Bluetooth Monitor - #478878
                          </h5>
                          <p className="card-text mb-0 justify-content-between d-flex unique-asset-meta">
                            <div>
                              <strong>Assigned on: </strong>
                              <span>22 Nov, 2022 10:32 AM</span>
                            </div>
                            <div>
                              <strong>Assigned by:</strong>
                              <span> Andrew Symon</span>
                            </div>
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div> */}
        </div>
      </div>
      <EmployeeEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        type={modalType}
        title={modalTitle}
        size={modalSize}
        control={control}
        handleSubmit={handleSubmit}
        onUpdate={onUpdate}
        register={register}
        errors={errors}
        editFields={currentFields}
        canEditCandidate={true}
      />
      <EmployeeCredentialEditModal
        show={credentialModal}
        onHide={() => setCredentialModal(false)}
        canEdit={canEditEmpCredential}
        employee={selectedEmployee}
        hasRolePermission={canViewRole}
        employeeId={idToUse}
      />
      <ToastContainer />
    </div>
  );
}
